import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import { up } from 'styled-breakpoints';
import styled, { useTheme, ThemeProvider } from 'styled-components';
import { SideBarZIndex } from 'src/components/constants';
import { ButtonV2 } from 'src/components/general/button/lgg-button';
import { ButtonVariant } from 'src/components/general/button/shared';
import { Icon } from 'src/components/general/icon';
import { FlexRow } from 'src/components/layout/flex-row';
import { useBreakpoint } from 'src/hooks/use-breakpoint';

const ConfirmationModalContentWrapper = styled.div<{
  $maxWidth?: number;
  $width?: string;
}>`
  background-color: white;
  border-radius: 6px;
  box-shadow: 0 4px 34px 0 rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 190px;
  justify-content: space-between;
  max-width: ${({ $maxWidth }) => ($maxWidth ? `${$maxWidth}px` : '420px')};
  pointer-events: all;
  width: ${({ $width }) => $width ?? 'max-content'};
`;

const ConfirmationModalTitle = styled.p`
  color: ${({ theme }) => theme.colors.carbon};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 24px;
  letter-spacing: -0.48px;
  line-height: 29px;
  margin: 0 0 10px;
  padding-right: 26px;
`;

const ConfirmationModalMessage = styled.div`
  color: ${({ theme }) => theme.colors.smalt};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 14px;
  line-height: 17px;
  margin: 0;
  padding-right: 16px;
`;

const ConfirmationModalBody = styled.div`
  padding: 20px;
`;

const ConfirmationModalFooter = styled(FlexRow)`
  border-top: 1px solid ${({ theme }) => theme.colors.koala};
  justify-content: flex-end;
  padding: 15px 20px;

  & > :not(:last-child) {
    margin-right: 15px;
  }
`;

const CloseIcon = styled(Icon)`
  cursor: pointer;
  position: absolute;
  right: 20px;
  top: 20px;

  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.casper};
    }
  }
`;

export type CloseConfirmationModal = (props?: { isSuccess: boolean }) => void;

type ConfirmationModalFooterButtonsProps = {
  onConfirm: VoidFunction;
  confirmButtonText: string;
  cancelButtonText?: string;
  confirmButtonType?: ButtonVariant;
  confirmButtonDisabled?: boolean;
  onCancel?: VoidFunction;
};

type ConfirmationModalFooterProps =
  | ({
      footerContent: (props: {
        onClose: CloseConfirmationModal;
      }) => React.ReactElement | null;
    } & Partial<Record<keyof ConfirmationModalFooterButtonsProps, never>>)
  | ({ footerContent?: undefined } & ConfirmationModalFooterButtonsProps);

type ConfirmationModalContentProps = {
  title: React.ReactElement | string;
  message: React.ReactElement | string;
  testId: string;
  onClose: CloseConfirmationModal;
  maxWidth?: number;
  width?: string;
} & ConfirmationModalFooterProps;

export const ConfirmationModalContent = (props: ConfirmationModalContentProps) => {
  const { title, message, testId, onClose, maxWidth, width } = props;
  const { t } = useTranslation(['common']);
  const breakpointUpMd = useBreakpoint(up('md'));
  const buttonSize = breakpointUpMd ? 'small' : 'regular';
  const customFooter = props.footerContent?.({ onClose });

  return (
    <ConfirmationModalContentWrapper
      data-lgg-id={testId}
      $maxWidth={maxWidth}
      $width={width}
    >
      <CloseIcon
        type="close"
        onClick={() => onClose()}
        data-lgg-id="confirm-modal-close-icon"
      />
      <ConfirmationModalBody>
        <ConfirmationModalTitle>{title}</ConfirmationModalTitle>
        <ConfirmationModalMessage>{message}</ConfirmationModalMessage>
      </ConfirmationModalBody>
      {customFooter !== null && (
        <ConfirmationModalFooter>
          {props.footerContent ? (
            customFooter
          ) : (
            <>
              <ButtonV2
                data-lgg-id="confirmation-modal-cancel-button"
                variant="default"
                size={buttonSize}
                onClick={() => {
                  props.onCancel?.();
                  onClose();
                }}
              >
                {props.cancelButtonText ?? t('common:cancel')}
              </ButtonV2>
              <ButtonV2
                data-lgg-id="confirmation-modal-confirm-button"
                variant={props.confirmButtonType ?? 'cta'}
                disabled={props.confirmButtonDisabled ?? false}
                size={buttonSize}
                onClick={() => {
                  props.onConfirm();
                  onClose({ isSuccess: true });
                }}
              >
                {props.confirmButtonText}
              </ButtonV2>
            </>
          )}
        </ConfirmationModalFooter>
      )}
    </ConfirmationModalContentWrapper>
  );
};

type ShowConfirmationModalProps = {
  title: React.ReactElement | string;
  message: React.ReactElement | string;
  testId: string;
  onClose?: CloseConfirmationModal;
  maxWidth?: number;
  width?: string;
} & ConfirmationModalFooterProps;

export const useShowConfirmationModal = () => {
  const theme = useTheme();

  const showConfirmationModal = useCallback(
    (props: ShowConfirmationModalProps) => {
      const confirmationModal = Modal.confirm({
        visible: false,
        modalRender: () => <></>,
      });

      const closeConfirmationModal = (closeProps?: { isSuccess: boolean }) => {
        props.onClose?.(closeProps);
        confirmationModal.destroy();
      };

      confirmationModal.update({
        maskClosable: true,
        visible: true,
        icon: null,
        centered: true,
        closable: true,
        // Putting these modals on top of the sidebar to prevent
        // them from appearing behind page's sidebar.
        zIndex: SideBarZIndex + 1,
        width: 'max-content',
        onCancel: closeConfirmationModal,
        modalRender: () => {
          return (
            <ThemeProvider theme={theme}>
              <ConfirmationModalContent
                {...props}
                onClose={(closeProps) => {
                  closeConfirmationModal(closeProps);
                }}
              />
            </ThemeProvider>
          );
        },
      });

      return confirmationModal;
    },
    [theme],
  );

  return showConfirmationModal;
};
